/**
 * @lodex-pro/web-shared/auth/use-auth — React hook exposing Amplify auth state.
 *
 * Phase 4 / Plan 04-09 — consumer apps (web-dispatch, web-billing, web-portal,
 * web-admin) import `useAuth` from `@lodex-pro/web-shared`, get back `{ user,
 * isAuthenticated, loading, signOutAll, refreshSession }`, and gate their routing
 * on `isAuthenticated`. The hook subscribes to Amplify's `Hub` channel so
 * sign-in/sign-out events from any other component (e.g. the TenantPicker's
 * `signOut` after switch) propagate to ALL mounted consumers without prop drilling.
 *
 * IMPORTANT: `useAuth` is a READ surface — it does NOT expose `signIn` / `confirmSignIn`.
 * Those primitives are exposed by the `EmailTotpFlow` component (Task 2), which owns
 * the multi-step Amplify v6 signIn state machine. Splitting the surfaces keeps the
 * hook's API stable as new sign-in modalities arrive (passkeys, magic-link, etc.).
 *
 * The hook initial state has `loading: true` until the first `getCurrentUser`+
 * `fetchAuthSession` round-trip completes. Consumers should render a spinner until
 * `loading === false`, then route based on `isAuthenticated`.
 *
 * Spec: master spec §7 (auth flows); CONTEXT D-05 (Amplify v6); RESEARCH § Pattern 4
 *       lines 350-381 (Amplify storage adapter wiring — Hub.listen is the auth-state
 *       propagation channel).
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchAuthSession, getCurrentUser, signOut, type AuthUser } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

import { restoreSessionFromRefreshCookie } from './amplify-bootstrap.js';
import { onRefreshStopped, resetRefreshGate } from './refresh-gate.js';

/**
 * Read-only auth state surfaced to consumer components. `user` is Amplify's
 * `AuthUser` (includes `userId` + `username` + `signInDetails`) or null when
 * not signed in. `isAuthenticated` is the consumer-facing boolean — a separate
 * field from `user !== null` so the loading state is unambiguous.
 */
export interface AuthState {
  readonly user: AuthUser | null;
  readonly isAuthenticated: boolean;
  readonly loading: boolean;
}

/**
 * Hook return type — `AuthState` plus two action callbacks. The actions are
 * `useCallback`-wrapped so consumer components can safely depend on them in
 * downstream `useEffect` arrays without triggering re-runs.
 */
export interface UseAuthResult extends AuthState {
  /**
   * Sign out across ALL devices (Cognito GlobalSignOut). Use this for the
   * "Sign out everywhere" admin affordance. The local `signOut({ global: false })`
   * path is exposed via the `signOut` import from `aws-amplify/auth` directly —
   * the TenantPicker uses it for the post-switch re-auth flow per Discretion #3.
   */
  readonly signOutAll: () => Promise<void>;
  /**
   * Fetch a fresh access token from the refresh cookie, through the tab's shared refresh
   * (refresh-gate.ts). The escape hatch for "I just changed something the JWT claims about
   * and need the new claims now" (e.g. after `/v1/auth/switch-tenant`). Not
   * `fetchAuthSession({ forceRefresh: true })`: Amplify cannot read the HttpOnly refresh
   * cookie, so that always failed and raised `tokenRefresh_failure` (OPEN-193).
   */
  readonly refreshSession: () => Promise<void>;
}

/**
 * `useAuth()` — single source of truth for the consumer app's auth gating.
 *
 * The initial mount triggers a `getCurrentUser` + `fetchAuthSession` to determine
 * whether the user is signed in. The Hub.listen subscription re-checks on `signedIn`,
 * `signedOut` and `tokenRefresh` ONLY, so the state stays in sync as the user signs in /
 * out / switches tenants. It ignores `tokenRefresh_failure` and every other event:
 * re-checking on a failure ran another refresh, which failed and raised another event,
 * a loop that sent ~10 refreshes a second for 7 minutes (OPEN-193). When the shared
 * refresh gives up (refresh-gate.ts), the hook shows the signed-out state.
 */
export function useAuth(): UseAuthResult {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    loading: true,
  });

  useEffect(() => {
    let mounted = true;

    const refresh = async (): Promise<void> => {
      try {
        const user = await getCurrentUser();
        const session = await fetchAuthSession();
        if (!mounted) return;
        if (session.tokens?.accessToken !== undefined) {
          setState({ user, isAuthenticated: true, loading: false });
          return;
        }
        // No in-memory token — fall through to the refresh-cookie restore.
      } catch {
        // getCurrentUser/fetchAuthSession threw (no in-memory session — e.g. after a
        // full page reload). Fall through to the refresh-cookie restore.
      }

      // Quick 260614-loop — boot-time session restore. The access/id tokens are kept
      // in-memory only and the refresh token is an HttpOnly cookie Amplify cannot read,
      // so a reload (incl. the post-login window.location redirect) leaves Amplify with
      // no session → the redirect loop. Restore via POST /v1/auth/refresh (cookie
      // auto-sent), then mark authenticated. API calls self-heal via authedFetch's
      // 401→refresh path regardless of whether Amplify's in-memory cache picks up the
      // primed token, so an authenticated gate here is correct.
      const restoredToken = await restoreSessionFromRefreshCookie();
      if (!mounted) return;
      if (restoredToken !== null) {
        let user: AuthUser | null = null;
        try {
          user = await getCurrentUser();
        } catch {
          user = null; // best-effort display identity; the gate only needs authenticated
        }
        if (!mounted) return;
        setState({ user, isAuthenticated: true, loading: false });
        return;
      }
      setState({ user: null, isAuthenticated: false, loading: false });
    };

    void refresh();

    // Re-check on these events only (OPEN-193): never on `tokenRefresh_failure`, which the
    // re-check itself can raise. A new sign-in sets a fresh refresh cookie, so it also clears
    // the shared refresh's backoff and stopped state.
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          resetRefreshGate();
          void refresh();
          return;
        case 'signedOut':
        case 'tokenRefresh':
          void refresh();
          return;
        default:
          return;
      }
    });

    // The shared refresh gave up (refresh cookie gone, or repeated 429/5xx): signed out.
    const unsubscribeStopped = onRefreshStopped(() => {
      if (mounted) setState({ user: null, isAuthenticated: false, loading: false });
    });

    return () => {
      mounted = false;
      unsubscribe();
      unsubscribeStopped();
    };
  }, []);

  const signOutAll = useCallback(async (): Promise<void> => {
    await signOut({ global: true });
  }, []);

  const refreshSession = useCallback(async (): Promise<void> => {
    await restoreSessionFromRefreshCookie();
  }, []);

  return { ...state, signOutAll, refreshSession };
}
